import { Component, Input, Output, EventEmitter, inject, ChangeDetectionStrategy } from '@angular/core';

import { FormGroup } from '@angular/forms';
import { MatButtonModule } from '@angular/material/button';
import { MatProgressSpinnerModule } from '@angular/material/progress-spinner';
import { MatTooltipModule } from '@angular/material/tooltip';
import {
  SchoolRosterStudent,
  UpdateSchoolRosterStudentRequest,
  RosterProgressStatuses,
  ToastMessageType
} from '@k12/models';
import { SchoolsService } from '@k12/school-services';
import { ToastMessageService } from '@k12/app-services';
import { ROSTER_ACTION_RESTRICTED_TOOLTIP } from '../restriction-tooltip-message';

@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  selector: 'lib-endorsement-actions',
  templateUrl: './endorsement-actions.component.html',
  styleUrl: './endorsement-actions.component.scss',
  standalone: true,
  imports: [MatButtonModule, MatProgressSpinnerModule, MatTooltipModule]
})
export class EndorsementActionsComponent {
  @Input() editable = false;
  @Input() student: SchoolRosterStudent | null = null;
  @Input() schoolEndorsementRestricted: boolean | null = null;
  @Input() form!: FormGroup;
  @Input() isNotAttending = false;
  @Input() individualCostErrors: string[] = [];
  @Output() endorsed = new EventEmitter<string>();
  @Output() validateForm = new EventEmitter<void>();
  @Output() syncFormToModel = new EventEmitter<void>();
  @Output() cancelForm = new EventEmitter<void>();
  @Output() withdrawalRequested = new EventEmitter<SchoolRosterStudent>();

  private readonly schoolsService = inject(SchoolsService);
  private readonly toastMessageService = inject(ToastMessageService);

  endorseChoice: 'yes' | 'no' | null = null;
  endorsing = false;
  readonly restrictionTooltipMessage = ROSTER_ACTION_RESTRICTED_TOOLTIP;

  get isEndorsementRestricted(): boolean {
    if (this.schoolEndorsementRestricted !== null) {
      return this.schoolEndorsementRestricted;
    }
    return this.student?.endorsementRestricted === true;
  }

  onCancel(): void {
    this.endorseChoice = null;
    this.form.get('attendanceTypeId')?.markAsUntouched();
    this.form.get('attendanceTypeId')?.updateValueAndValidity();
    this.cancelForm.emit();
  }

  onEndorseChoiceSelect(choice: 'yes' | 'no'): void {
    if (this.isEndorsementRestricted) return;
    this.endorseChoice = choice;
    // Trigger validation when selecting 'yes' for attending students
    if (choice === 'yes' && !this.isNotAttending) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      this.validateForm.emit();
    }
  }

  onEndorse(): void {
    if (this.isEndorsementRestricted) return;
    if (!this.canEndorse()) return;
    if (this.endorseChoice === 'no') {
      if (!this.student) return;
      this.withdrawalRequested.emit(this.student);
    } else {
      this.processEndorsement();
    }
  }

  private canEndorse(): boolean {
    if (this.isEndorsementRestricted) return false;
    if (this.endorsing) return false;

    // Only validate form if it's editable
    if (this.editable) {
      this.form.markAllAsTouched();
      this.form.updateValueAndValidity();
      this.validateForm.emit();
      if (this.form.invalid || this.individualCostErrors.length > 0 || !this.student || !this.endorseChoice) {
        return false;
      }
    } else if (!this.student || !this.endorseChoice) {
      return false;
    }
    return true;
  }

  private processEndorsement(): void {
    this.endorsing = true;
    // Only sync form to model if form is editable
    if (this.editable) {
      this.syncFormToModel.emit();
    }
    if (!this.student || !this.endorseChoice) return;
    const dto = this.createEndorsementDto(this.endorseChoice);
    const successMessage =
      this.endorseChoice === 'yes' ? 'Student endorsed successfully' : 'Student marked as not endorsed';
    this.submitEndorsement(dto, successMessage);
  }

  private createEndorsementDto(endorseChoice: 'yes' | 'no'): UpdateSchoolRosterStudentRequest {
    if (!this.student) throw new Error('No student available');
    const localDateTime = this.getLocalISODateTime();

    // Build termPayments array from crossTermSummary
    // Exclude past terms that have no existing enrollment — they won't be created
    const now = new Date();
    const termCosts = (this.student.crossTermSummary || [])
      .filter((term) => term.studentEnrollmentId || new Date(term.endDate) >= now)
      .map((term) => ({
        studentEnrollmentId: term.studentEnrollmentId || null,
        schoolTermGradeId: term.schoolTermGradeId,
        termCost: term.payment
      }));

    // Determine roster progress status based on endorsement choice
    const rosterProgressStatusId =
      endorseChoice === 'yes' ? RosterProgressStatuses.Endorsed : RosterProgressStatuses.Canceled;

    return {
      studentEnrollmentId: this.student.studentEnrollmentId,
      studentId: this.student.studentId,
      schoolProgramPartnerId: this.student.schoolProgramPartnerId,
      schoolTermGradeId: this.student.schoolTermGradeId || '',
      attendanceTypeId: this.student.attendanceTypeId,
      rosterProgressStatusId: rosterProgressStatusId,
      academicYearStandardCostTotal: this.student.academicYearStandardCostTotal,
      academicYearIndividualCostTotal: this.student.academicYearIndividualCostTotal,
      termCost: this.getPrimaryTermCost(),
      termCosts: termCosts,
      certificationDate: this.student.certificationDate || null,
      endorsedDate: endorseChoice === 'yes' ? localDateTime : null,
      startDate: this.student.startDate,
      endDate: endorseChoice === 'no' ? localDateTime : null
    } as UpdateSchoolRosterStudentRequest;
  }

  private submitEndorsement(dto: UpdateSchoolRosterStudentRequest, successMessage: string): void {
    this.schoolsService.updateSchoolRosterStudent(dto).subscribe({
      next: (response) => {
        this.endorsing = false;
        if (response?.success) {
          if (this.student?.studentEnrollmentId) {
            this.endorsed.emit(this.student.studentEnrollmentId);
          }
          this.toastMessageService.showToast(ToastMessageType.Success, successMessage);
        } else {
          this.toastMessageService.showToast(
            ToastMessageType.Error,
            response?.message || `Failed to ${successMessage.toLowerCase()}`
          );
        }
      },
      error: () => {
        this.endorsing = false;
        this.toastMessageService.showToast(ToastMessageType.Error, `Failed to ${successMessage.toLowerCase()}`);
      }
    });
  }

  private getPrimaryTermCost(): number | null | undefined {
    if (!this.student) return undefined;
    return (this.student.crossTermSummary || []).find((t) => t.schoolTermGradeId === this.student!.schoolTermGradeId)
      ?.payment;
  }

  private getLocalISODateTime(): string {
    const now = new Date();
    return new Date(now.getTime() - now.getTimezoneOffset() * 60000).toISOString();
  }

  get isEndorseDisabled(): boolean {
    if (this.isEndorsementRestricted) return true;
    if (this.isNotAttending) return !this.endorseChoice || this.endorsing;
    // In read-only mode, don't check form validity - user can't edit to fix errors anyway
    if (!this.editable) return !this.endorseChoice || this.endorsing;
    return !this.endorseChoice || this.form.invalid || this.individualCostErrors.length > 0 || this.endorsing;
  }
}
